import isNil from 'lodash/isNil';
import {
    renovationTypeEnumMapRenovationType,
    bathroomEnumMapGraphQLBathroomType,
    areaUnitEnumMapGraphQLAreaUnit,
    wallsTypeEnumMapGraphQLWallsType
} from '@search/filter-enums/gql/enum-mappings';
import {
    ProfOfferFlat,
    BathroomEnum,
    AreaUnitEnum,
    ProfOfferLivingFacilities,
    WallsTypeEnum,
    ProfOfferInfrastructure,
    ProfOfferInfrastructureParking,
    ProfOfferInfrastructureParkingType,
    ProfOfferRoom,
    ProfOfferLand,
    ProfOfferSupply,
    ProfOfferHouse,
    BathroomLocationEnum,
    RentLivingTenantRequirements,
    WindowsDirectionOfferEnum,
    HouseCategory,
    HouseCategoryEnum,
    RenovationTypeEnum,
    LandStatusEnum,
    BuildHeatingTypeEnum,
    BuildEpochEnum,
    InfrastructureEntitiesListEnum,
    YardEntityEnum,
    NbFinishingType
} from '@search/graphql-typings';
import { formatLocaleNumber } from '@search/helpers/src/formatNumber';
import { declensionByNumber } from '@search/helpers/src/declensionByNumber';
import { areaUnitMapping } from '@search/filter-enums/mappings/area_unit_mapping';
import { renovationTypeMapping } from '@search/filter-enums/mappings/renovationTypeMapping';
import { bathroomTypeMapping } from '@search/filter-enums/mappings/bathroomTypeMapping';
import { wallsTypeMapping } from '@search/filter-enums/mappings/walls_type_mapping';
import { InfoModel, InfoModelRaw, InformationsEnum, InformationsIconEnum } from './types/InfoModel';

// Комнатность
export const getRoomsCountInFlat = (flat?: ProfOfferFlat): null | string => {
    return isNil(flat?.roomsCount) ?
        null :
        flat!.roomsCount!.toString();
};

// Отделка
export const getRenovationType = (
    flat: ProfOfferFlat,
    isNewBuilding?: boolean,
): InfoModel[] => {
    if (isNewBuilding) {
        const finishingType: null | NbFinishingType = isNil(flat?.characteristics?.nbFinishingType) ?
            null :
            flat!.characteristics!.nbFinishingType;

        if (finishingType === null) return [];

        const finishingInfo: InfoModel = {
            title: 'Отделка',
            value: finishingType.text,
            key: InformationsEnum.finishing,
        };

        return [ finishingInfo ];
    }

    const renovationType: null | RenovationTypeEnum = isNil(flat?.characteristics?.renovationType) ?
        null :
        flat!.characteristics!.renovationType;

    if (renovationType === null || renovationType === RenovationTypeEnum.Unknown) {
        return [];
    }

    const renovationInfo: InfoModel = {
        title: 'Ремонт',
        value: renovationTypeMapping[renovationTypeEnumMapRenovationType[renovationType]],
        key: InformationsEnum.renovationType
    };

    switch (renovationType) {
        case RenovationTypeEnum.Design:
            renovationInfo.icon = InformationsIconEnum.renovationDesign;
            break;
        case RenovationTypeEnum.Euro:
            renovationInfo.icon = InformationsIconEnum.renovationEuro;
            break;
        case RenovationTypeEnum.Fine:
        case RenovationTypeEnum.PreFine:
            renovationInfo.icon = InformationsIconEnum.renovationFine;
            break;
        case RenovationTypeEnum.Good:
            renovationInfo.icon = InformationsIconEnum.renovationGood;
            break;
        case RenovationTypeEnum.Need:
        case RenovationTypeEnum.Partial:
            renovationInfo.icon = InformationsIconEnum.renovationNeed;
            break;
        case RenovationTypeEnum.Rough:
        default:
            renovationInfo.icon = InformationsIconEnum.renovationRough;
    }

    return [ renovationInfo ];
};

// Санузел
export const getBathroomB2c = (flat?: ProfOfferFlat) => {
    const bathroomCount = flat?.characteristics?.bathroomCount ?? 0;
    const bathroomType = isNil(flat?.characteristics?.bathroomType) ?
        null :
        bathroomTypeMapping[
            bathroomEnumMapGraphQLBathroomType[
                  flat!.characteristics!.bathroomType! as Exclude<BathroomEnum, BathroomEnum.Several>
            ]
        ];

    let icon: InformationsIconEnum = InformationsIconEnum.bathroomCombined;

    if (flat?.characteristics?.bathroomType === BathroomEnum.Parted) {
        icon = InformationsIconEnum.bathroomNotCombined;
    }

    return [
        bathroomType && bathroomType !== BathroomEnum.Unknown ? {
            icon,
            key: InformationsEnum.bathroom,
            title: 'Санузел',
            value: bathroomType
        } : null,
        bathroomCount ? {
            icon,
            key: InformationsEnum.bathroomCount,
            title: 'Кол-во санузлов',
            value: bathroomCount
        } : null
    ].filter(Boolean) as InfoModel[];
};

// Санузел
export const getBathroomB2b = (flat?: ProfOfferFlat): InfoModel[] => {
    const bathroomCount = flat?.characteristics?.bathroomCount ?? 0;
    const bathroomType = isNil(flat?.characteristics?.bathroomType) ?
        null :
        bathroomTypeMapping[
            bathroomEnumMapGraphQLBathroomType[
                  flat!.characteristics!.bathroomType! as Exclude<BathroomEnum, BathroomEnum.Several>
            ]
        ];

    const countBathroomText = bathroomCount > 1 ?
        `${bathroomCount} ${declensionByNumber(bathroomCount, [ 'санузел', 'санузла', 'санузлов' ])}` :
        '';

    const bathroomText = [ bathroomType, countBathroomText ].filter(Boolean);

    if (bathroomText.length <= 0) {
        return [];
    }

    return [ {
        title: 'Санузел',
        value: bathroomText.join(', '),
        key: InformationsEnum.bathroom
    } ];
};

// Площадь
export const getTotalArea = (flat?: ProfOfferFlat): null | string => {
    if (flat?.totalArea?.formatted) return flat.totalArea.formatted;

    const value = flat?.totalAreaValue ?? null;

    if (value === null) {
        return null;
    }

    const unitText = areaUnitMapping[areaUnitEnumMapGraphQLAreaUnit[flat?.totalAreaUnit ?? AreaUnitEnum.Meter]];

    return `${formatLocaleNumber(value)} ${unitText}`;
};

// Жилая площадь
export const getLivingArea = (flat?: ProfOfferFlat): null | string => {
    if (flat?.livingArea?.formatted) return flat.livingArea.formatted;

    const value = flat?.livingAreaValue ?? null;

    if (value === null) {
        return null;
    }

    const unitText = areaUnitMapping[areaUnitEnumMapGraphQLAreaUnit[flat?.livingAreaUnit ?? AreaUnitEnum.Meter]];

    return `${formatLocaleNumber(value)} ${unitText}`;
};

// Площадь кухни
export const getKitchenArea = (flat?: ProfOfferFlat): null | string => {
    if (flat?.kitchenArea?.formatted) return flat.kitchenArea.formatted;

    const value = flat?.kitchenArea?.value ?? null;

    if (value === null) {
        return null;
    }

    const unitText = areaUnitMapping[areaUnitEnumMapGraphQLAreaUnit[flat?.kitchenArea?.unit ?? AreaUnitEnum.Meter]];

    return `${formatLocaleNumber(value)} ${unitText}`;
};

// Этаж
export const getFloor = (flat?: ProfOfferFlat): {
    floorText: string;
    floorText2: string;
    floor: number;
    isFirstFloor: boolean;
    isLastFloor: boolean;
} | null => {
    const floor: null | number = isNil(flat?.floor) ? null : flat!.floor;
    const floorsTotal: null | number = flat?.building?.floorsTotal ? flat!.building!.floorsTotal : null;

    if (floorsTotal === null || floor === null) {
        return null;
    }

    const floorText = `${floor} из ${floorsTotal}`;
    const floorText2 = `${floor}/${floorsTotal}`;

    return {
        floor,
        floorText,
        floorText2,
        isFirstFloor: floor === 1,
        isLastFloor: floorsTotal === floor,
    };
};

export function getBalcony(flat?: ProfOfferFlat): null | 'есть' {
    return flat?.characteristics?.hasBalcony === true ? 'есть' : null;
}

export function getLivingFacilities(livingFacilities?: ProfOfferLivingFacilities | null): InfoModel[] {
    const results: InfoModel[] = [];

    if (isNil(livingFacilities)) {
        return results;
    }

    if (livingFacilities.hasAirConditioner === true) {
        results.push({
            title: 'Кондиционер',
            value: 'есть',
            key: InformationsEnum.hasAirConditioner
        });
    }

    if (livingFacilities.hasDishwasher === true) {
        results.push({
            title: 'Посудомойка',
            value: 'есть',
            key: InformationsEnum.hasDishwasher
        });
    }

    if (livingFacilities.hasFridge === true) {
        results.push({
            title: 'Холодильник',
            value: 'есть',
            key: InformationsEnum.hasFridge
        });
    }

    if (livingFacilities.hasFurniture !== null && livingFacilities.hasFurniture !== undefined) {
        results.push({
            title: 'Мебель',
            value: booleanStr(livingFacilities.hasFurniture),
            key: livingFacilities.hasFurniture ? InformationsEnum.hasFurniture : InformationsEnum.noFurniture
        });
    }

    if (livingFacilities.hasInternet) {
        results.push({
            title: 'Интернет',
            value: 'есть',
            key: InformationsEnum.hasInternet
        });
    }

    if (livingFacilities.hasKitchenFurniture) {
        results.push({
            title: 'Кухонная мебель',
            value: 'есть',
            key: InformationsEnum.hasKitchenFurniture
        });
    }

    if (livingFacilities.hasTv) {
        results.push({
            title: 'Телевизор',
            value: 'есть',
            key: InformationsEnum.hasTv
        });
    }

    if (livingFacilities.hasWashingMachine) {
        results.push({
            title: 'Стиральная машина',
            value: 'есть',
            key: InformationsEnum.hasWashingMachine
        });
    }

    return results;
}

// Срок сдачи
export const getFinishQuarter = (flat?: ProfOfferFlat): string | undefined => {
    return flat?.building?.newBuildingStatus?.formattedInProgress;
};

// Год постройки
export const getBuildYear = (flat?: ProfOfferFlat): undefined | string => {
    return flat?.building?.newBuildingStatus?.builtYear?.toString();
};

// Программа реновации
export const getRenovationProgram = (isRenovationProgram?: boolean): null | 'Подлежит реновации' => {
    return isRenovationProgram ? 'Подлежит реновации' : null;
};

// Высота потолков
export const getCeilingHeightM = (flat?: ProfOfferFlat): null | string => {
    const ceilingHeightM: null | string = isNil(flat?.characteristics?.ceilingHeightM) ?
        null :
        flat!.characteristics!.ceilingHeightM!;

    if (ceilingHeightM === null) {
        return null;
    }

    return `${ceilingHeightM} м`;
};

// Лифт
export const getHasLift = (flat?: ProfOfferFlat): null | 'есть' => {
    return flat?.building?.hasLift === true ? 'есть' : null;
};

// Материал стен
export const getWallsType = (flat?: ProfOfferFlat): InfoModelRaw[] => {
    const wallsType: null | WallsTypeEnum = isNil(flat?.building?.wallsType) ? null : flat!.building?.wallsType!;

    if (wallsType === null || wallsType === WallsTypeEnum.Unknown) {
        return [];
    }

    const wallInfo: InfoModel = {
        title: 'Материал стен',
        value: wallsTypeMapping[wallsTypeEnumMapGraphQLWallsType[wallsType]],
        key: InformationsEnum.wallsType
    };

    switch (wallsType) {
        case WallsTypeEnum.Block:
            wallInfo.icon = InformationsIconEnum.blockWalls;
            break;
        case WallsTypeEnum.Brick:
            wallInfo.icon = InformationsIconEnum.brickWalls;
            break;
        case WallsTypeEnum.BrickMonolith:
            wallInfo.icon = InformationsIconEnum.brickMonolithWalls;
            break;
        case WallsTypeEnum.Monolith:
            wallInfo.icon = InformationsIconEnum.monolithWalls;
            break;
        case WallsTypeEnum.Panel:
            wallInfo.icon = InformationsIconEnum.panelWalls;
            break;
        case WallsTypeEnum.Wood:
        default:
            wallInfo.icon = InformationsIconEnum.woodWalls;
    }

    return [ wallInfo ];
};

export const getConsierge = (infrastructure?: ProfOfferInfrastructure): 'есть' | null => {
    return infrastructure?.hasConcierge === true ? 'есть' : null;
};

export const getParking = (infrastructure?: ProfOfferInfrastructure): InfoModel[] => {
    const info: InfoModel[] = [];
    // @ts-ignore
    const parkingList: ProfOfferInfrastructureParking[] | null = isNil(infrastructure?.parking) ?
        null :
        infrastructure?.parking!;

    if (parkingList === null) {
        return info;
    }

    parkingList.forEach(parking => {
        if (parking.type! === ProfOfferInfrastructureParkingType.Closed) {
            info.push({
                title: 'Парковка',
                value: 'закрытая',
                key: InformationsEnum.closedParking
            });
        } else if (parking.type === ProfOfferInfrastructureParkingType.Open) {
            info.push({
                title: 'Парковка',
                value: 'открытая',
                key: InformationsEnum.openParking
            });
        } else if (parking.type === ProfOfferInfrastructureParkingType.Underground) {
            info.push({
                title: 'Парковка',
                value: 'подземная',
                key: InformationsEnum.undergroundParking
            });
        }
    });

    return info;
};

export const getClosedArea = (infrastructure?: ProfOfferInfrastructure): InfoModel[] => {
    return infrastructure?.hasGuardedArea === true ?
        [ {
            title: 'Территория',
            value: 'закрытая',
            key: InformationsEnum.closedArea
        } ] :
        [];
};

export const getApartments = (flat?: ProfOfferFlat): 'да' | null => {
    return flat?.isApartments === true ? 'да' : null;
};

/** Комнат на продажу */
export function getFlatRoomInfo(room: ProfOfferRoom): null | string {
    const rooms = room.roomCount;

    if (isNil(rooms)) {
        return null;
    }

    return rooms.toString();
}

/** Площадь комнат на продажу */
export function getLivingAreaRoom(roomOffer: ProfOfferRoom): null | string {
    return isNil(roomOffer?.roomsLivingAreaFormatted) ? null : roomOffer.roomsLivingAreaFormatted;
}

/** Площадь участка */
export const getLandArea = (land: ProfOfferLand): null | { landText: string; land?: number } => {
    return isNil(land.area?.value) ?
        null :
        {
            landText: land.area!.formatted,
            land: land.area?.value
        };
};

/** Статус участка */
export const getLandStatus = (land: ProfOfferLand, isMobile?: boolean): InfoModel[] => {
    const status = isNil(land.status2) || land.status2?.value === LandStatusEnum.Unknown ?
        null :
        land.status2;

    if (status === null) return [];

    const statusInfo: InfoModel = {
        title: isMobile ? 'Статус' : 'Статус участка',
        value: status.formatted,
        key: InformationsEnum.landStatus
    };

    switch (status.value) {
        case LandStatusEnum.Snt:
        case LandStatusEnum.Dnp:
            statusInfo.icon = InformationsIconEnum.landSntOrDnp;
            statusInfo.uppercaseValue = true;
            break;
        case LandStatusEnum.Izhs:
            statusInfo.icon = InformationsIconEnum.landIzhs;
            statusInfo.uppercaseValue = true;
            break;
        case LandStatusEnum.Agricultural:
            statusInfo.icon = InformationsIconEnum.landLph;
            statusInfo.uppercaseValue = true;
            break;
        case LandStatusEnum.Industrial:
        case LandStatusEnum.Invest:
        default:
            statusInfo.icon = InformationsIconEnum.generalHas;
    }

    return [ statusInfo ];
};

/** Коммуникации */
export function getLandSupply(supply?: ProfOfferSupply | null): InfoModel[] {
    const results: InfoModel[] = [];

    if (supply) {
        if (supply.hasGas !== null && supply.hasGas !== undefined) {
            results.push({
                title: 'Газ',
                value: booleanStr(supply.hasGas),
                key: InformationsEnum.gas
            });
        }

        if (supply.hasElectricity !== null && supply.hasElectricity !== undefined) {
            results.push({
                title: 'Электричество',
                value: booleanStr(supply.hasElectricity),
                key: InformationsEnum.electricity
            });
        }

        if (supply.hasSewerage !== null && supply.hasSewerage !== undefined) {
            results.push({
                title: 'Канализация',
                value: booleanStr(supply.hasSewerage),
                key: InformationsEnum.sewerage
            });
        }

        if (supply.hasWater !== null && supply.hasWater !== undefined) {
            results.push({
                title: 'Вода',
                value: booleanStr(supply.hasWater),
                key: InformationsEnum.water
            });
        }
    }

    return results;
}

/** Площадь дома */
export const getHouseArea = (house: ProfOfferHouse): null | string => {
    return isNil(house.totalArea?.formatted) ? null : house.totalArea!.formatted;
};

/** Санузел/ванная в доме **/
export const getBathroomInHouse = (house: ProfOfferHouse): InfoModel[] => {
    if (house.hasBathroom === false) {
        return [ {
            title: 'Санузел',
            value: 'нет',
            key: InformationsEnum.houseHasBathroom,
            icon: InformationsIconEnum.generalHasNot
        } ];
    }

    const bathroomLocationEnum: null | BathroomLocationEnum = isNil(house.bathroomLocationType) ?
        null :
        house!.bathroomLocationType;

    if (bathroomLocationEnum === BathroomLocationEnum.Unknown || bathroomLocationEnum === null) {
        return [];
    }

    return bathroomLocationEnum === BathroomLocationEnum.Inside ?
        [ {
            title: 'Санузел',
            value: 'в доме',
            key: InformationsEnum.houseBathroomLocation,
            icon: InformationsIconEnum.houseBathroomIn
        } ] :
        [ {
            title: 'Санузел',
            value: 'на улице',
            key: InformationsEnum.houseBathroomLocation,
            icon: InformationsIconEnum.houseBathroomOut
        } ];
};

/** Год постройки дома */
export const getBuildYearHouse = (house: ProfOfferHouse): null | string => {
    const buildYear: null | number = isNil(house.builtYear) ? null : house!.builtYear;

    if (buildYear === 0 || buildYear === null) {
        return null;
    }

    return buildYear?.toString();
};

/** Этажность дома */
export const getFloorsHouse = (house: ProfOfferHouse): null | string => {
    return isNil(house.floorsTotal) || house.floorsTotal === 0 ? null : house!.floorsTotal.toString();
};

/** Комнатность в доме */
export const getRoomsInHouse = (house: ProfOfferHouse): null | string => {
    const roomsCount: null | number = isNil(house.roomsCount) ? null : house!.roomsCount;

    if (roomsCount === 0 || roomsCount === null) {
        return null;
    }

    return roomsCount.toString();
};

/** Тип дома */
export const getHouseType = (house: ProfOfferHouse, isMobile?: boolean): InfoModel[] => {
    const houseCategory: null | HouseCategory = isNil(house.category2) ? null : house!.category2;

    if (houseCategory === null || houseCategory.value === HouseCategoryEnum.Unknown) {
        return [];
    }

    const houseInfo: InfoModel = {
        title: isMobile ? 'Объект' : 'Тип дома',
        value: houseCategory.formatted,
        key: InformationsEnum.houseCategory
    };

    switch (houseCategory.value) {
        case HouseCategoryEnum.Duplex:
            houseInfo.icon = InformationsIconEnum.duplex;
            break;
        case HouseCategoryEnum.PartOfHouse:
            houseInfo.icon = InformationsIconEnum.partHouse;
            break;
        case HouseCategoryEnum.Standalone:
            houseInfo.icon = InformationsIconEnum.singleHouse;
            break;
        case HouseCategoryEnum.Townhouse:
        default:
            houseInfo.icon = InformationsIconEnum.townhouse;
    }

    return [ houseInfo ];
};

/** Отопление в доме */
export const getHeating = (house: ProfOfferHouse): InfoModel[] => {
    const results: InfoModel[] = [];

    if (house.hasHeating !== null && house.hasHeating !== undefined) {
        results.push({
            title: 'Отопление',
            value: booleanStr(house.hasHeating),
            key: InformationsEnum.houseHeating
        });
    }

    return results;
};

export const getTenantsInfoB2c = (tenants?: RentLivingTenantRequirements | null): InfoModel[] => {
    const result: InfoModel[] = [];

    if (tenants?.childrenAllowed?.formatted) {
        result.push({
            value: tenants.childrenAllowed.formatted,
            title: 'Можно жить',
            key: InformationsEnum.children
        });
    }
    if (tenants?.petsAllowed?.formatted) {
        result.push({
            value: tenants.petsAllowed.formatted,
            title: 'Можно жить',
            key: InformationsEnum.pets
        });
    }

    return result;
};

export const getTenantsInfoB2b = (tenants?: RentLivingTenantRequirements | null): InfoModel[] => {
    const values: string[] = [];

    if (tenants?.childrenAllowed?.formatted && tenants?.petsAllowed?.formatted) {
        values.push('с детьми/животными');
    } else if (tenants?.petsAllowed?.formatted) {
        values.push(tenants.petsAllowed!.formatted!);
    } else if (tenants?.childrenAllowed?.formatted) {
        values.push(tenants.childrenAllowed!.formatted!);
    }

    const result: InfoModel[] = [];

    if (values.length > 0) {
        result.push({
            title: 'Можно\u00A0жить',
            value: values.join(', '),
            key: InformationsEnum.tenants
        });
    }

    return result;
};

export const getWindowsDirection = (flat?: ProfOfferFlat): InfoModel[] => {
    const windowsDirection = flat?.characteristics?.windowsDirection;
    const result: string[] = [];

    if (windowsDirection === WindowsDirectionOfferEnum.Rear || windowsDirection === WindowsDirectionOfferEnum.Both) {
        result.push('во двор');
    }

    if (windowsDirection === WindowsDirectionOfferEnum.Front || windowsDirection === WindowsDirectionOfferEnum.Both) {
        result.push('на улицу');
    }

    const resultText = result.join(' и ');

    return result.length > 0 ?
        [ {
            title: `Вид из ок${result.length > 1 ? 'он' : 'на'}`,
            value: resultText,
            key: InformationsEnum.windowsDirection
        } ] :
        [];
};

export const getGarbage = (flat?: ProfOfferFlat): InfoModel[] => {
    return flat?.building?.hasGarbageChute ?
        [ {
            title: 'Мусоропровод',
            value: 'есть',
            key: InformationsEnum.garbage
        } ] :
        [];
};

function booleanStr(has: boolean) {
    return has ? 'есть' : 'нет';
}

export const getBuildEpoch = (epoch?: BuildEpochEnum) => {
    switch (epoch) {
        case BuildEpochEnum.Stalin:
            return 'сталинка';
        case BuildEpochEnum.Khrushchev:
            return 'хрущевка';
        case BuildEpochEnum.Brezhnev:
            return 'брежневка';
        default:
            return null;
    }
};

export const getHeatingType = (heatingType?: BuildHeatingTypeEnum) => {
    switch (heatingType) {
        case BuildHeatingTypeEnum.Central:
            return 'центральное';
        case BuildHeatingTypeEnum.BoilerRoom:
            return 'домовая котельная';
        default:
            return null;
    }
};

export const getFitnessClub = (list: InfrastructureEntitiesListEnum[]) => {
    if (list.includes(InfrastructureEntitiesListEnum.FitnessClub)) return 'Фитнес клуб';

    return null;
};

export const getKindergarten = (list: InfrastructureEntitiesListEnum[]) => {
    if (list.includes(InfrastructureEntitiesListEnum.Kindergarten)) return 'Детский сад';

    return null;
};

export const getPark = (list: InfrastructureEntitiesListEnum[]) => {
    if (list.includes(InfrastructureEntitiesListEnum.Park)) return 'Парк или зона отдыха';

    return null;
};

export const getShoppingMall = (list: InfrastructureEntitiesListEnum[]) => {
    if (list.includes(InfrastructureEntitiesListEnum.ShoppingMall)) return 'Торговый центр';

    return null;
};

export const getGoodSchools = (list: InfrastructureEntitiesListEnum[], isPluralForm?: boolean) => {
    if (list.includes(InfrastructureEntitiesListEnum.School)) return isPluralForm ? 'Хорошие школы' : 'Хорошая школа';

    return null;
};

export const getSportsGround = (list: YardEntityEnum[]) => {
    if (list.includes(YardEntityEnum.SportsGround)) return 'Спортивная площадка';

    return null;
};
